import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { usePresence } from 'framer-motion';

const stack: HTMLDialogElement[] = [];
let bodyOverflow = '';
export const dialogLayerEvent = 'pos-dialog-layer-change';
export const activeDialog = () => stack[stack.length - 1] || document.body;

// Native modal dialogs put the background (including lower dialogs) in the
// browser's inert top layer. Portals avoid transformed/overflowing ancestors.
export const Dialog = ({ children, labelledBy, onClose, busy = false, focusKey, className = '' }: {
  children: React.ReactNode; labelledBy: string; onClose: () => void;
  busy?: boolean; focusKey?: string | number; className?: string;
}) => {
  const ref = useRef<HTMLDialogElement>(null);
  const [isPresent, safeToRemove] = usePresence();
  const latest = useRef({ onClose, busy });
  latest.current = { onClose, busy };
  const focusTitle = useCallback(() => ref.current?.querySelector<HTMLElement>('[id="' + labelledBy + '"]')?.focus(), [labelledBy]);

  useLayoutEffect(() => {
    if (!isPresent) return;
    const dialog = ref.current!;
    const opener = document.activeElement as HTMLElement | null;
    if (!stack.length) bodyOverflow = document.body.style.overflow;
    dialog.style.display = '';
    dialog.showModal();
    stack.push(dialog);
    document.body.style.overflow = 'hidden';
    focusTitle();
    document.dispatchEvent(new Event(dialogLayerEvent));
    const observer = new MutationObserver(() => {
      // Removing a focused row/control must not strand focus on the inert page.
      if (activeDialog() === dialog && !dialog.contains(document.activeElement)) focusTitle();
    });
    observer.observe(dialog, { childList: true, subtree: true });
    const cancel = (event: Event) => {
      event.preventDefault();
      if (!latest.current.busy) latest.current.onClose();
    };
    dialog.addEventListener('cancel', cancel);
    return () => {
      observer.disconnect();
      dialog.removeEventListener('cancel', cancel);
      const wasTop = activeDialog() === dialog;
      stack.splice(stack.indexOf(dialog), 1);
      dialog.close();
      // Utility display classes must not expose a closed dialog during exit.
      dialog.style.display = 'none';
      if (!stack.length) document.body.style.overflow = bodyOverflow;
      document.dispatchEvent(new Event(dialogLayerEvent));
      if (wasTop) {
        const restore = () => {
          if (opener?.isConnected && !opener.closest('[inert]')) opener.focus();
          if (!opener?.isConnected || document.activeElement === document.body) {
            const fallback = stack.length ? activeDialog().querySelector<HTMLElement>('h2')
              : document.querySelector<HTMLElement>('[data-dialog-fallback], main h1, h1, h2');
            if (fallback) { fallback.tabIndex = -1; fallback.focus(); }
          }
        };
        restore();
        // Some browsers finish top-layer focus changes at the next frame.
        // Preserve intentional focus placed by a success receipt/new dialog.
        requestAnimationFrame(() => {
          if (document.activeElement === document.body || dialog.contains(document.activeElement)) restore();
        });
      }
    };
    // Callbacks and busy state are read through latest; opening has one lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPresent]);

  // Release native modality before a success receipt or route can take focus.
  useEffect(() => { if (!isPresent) safeToRemove?.(); }, [isPresent, safeToRemove]);

  useLayoutEffect(() => { focusTitle(); }, [focusKey, focusTitle]);

  return createPortal(<dialog ref={ref} aria-modal="true" aria-labelledby={labelledBy} aria-busy={busy}
    className={'fixed inset-0 m-0 h-[100dvh] w-screen max-w-none max-h-none border-0 bg-transparent p-0 text-inherit backdrop:bg-transparent ' + className}
    onKeyDown={event => {
      if (event.key !== 'Tab' || activeDialog() !== ref.current) return;
      const nodes = Array.from(ref.current.querySelectorAll<HTMLElement>('button, input, select, textarea, a[href], [tabindex]'))
        .filter(node => node.tabIndex >= 0 && !node.matches(':disabled') && !node.closest('[hidden], [inert]') && node.getClientRects().length > 0);
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (!first) { event.preventDefault(); focusTitle(); return; }
      if (event.shiftKey && (document.activeElement === first || !nodes.includes(document.activeElement as HTMLElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !nodes.includes(document.activeElement as HTMLElement))) {
        event.preventDefault(); first.focus();
      }
    }}>
    {children}
    {busy && <p role="status" className="sr-only">Guardando. Espera antes de cerrar.</p>}
  </dialog>, document.body);
};
